/* global Office, window, process */
import type { AppDispatch } from "../../store";
import { SSOAuthApi } from "../../api/sso-auth-api";
import { isAuthAccessDeniedError } from "../../api/auth-errors";
import { clearSession, setAccessDenied, setSession } from "../../slices/authSlice";
import { isTrustedDialogMessage, parseLoginMessage } from "../../utils/sdaLoginMessage";

function getAddinUrl(path: string): string {
  const baseUrl = process.env.APP_BASE_URL || window.location.origin;
  return `${baseUrl.replace(/\/$/, "").replace(/\/taskpane\.html$/i, "")}/${path}`;
}

export async function completeSdaLoginFromMessage(
  dispatch: AppDispatch,
  message: string
): Promise<"success" | "access_denied" | "failed"> {
  try {
    const payload = parseLoginMessage(message);
    const session = await dispatch(SSOAuthApi.endpoints.exchangeCode.initiate(payload)).unwrap();
    dispatch(setSession(session));
    return "success";
  } catch (error) {
    if (isAuthAccessDeniedError(error)) {
      dispatch(setAccessDenied());
      return "access_denied";
    }
    dispatch(clearSession());
    return "failed";
  }
}

export function openSdaLoginDialog(
  isOfficeInitialized: boolean,
  dispatch: AppDispatch,
  onError: (message: string) => void
): void {
  const dialogUrl = getAddinUrl("login.html");
  if (isOfficeInitialized && Office?.context?.ui) {
    Office.context.ui.displayDialogAsync(dialogUrl, (asyncResult) => {
      if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
        onError(asyncResult.error?.message || "Unable to open the SSO login window.");
        return;
      }
      const dialog = asyncResult.value;
      dialog.addEventHandler(Office.EventType.DialogMessageReceived, (dialogResponse: any) => {
        if (dialogResponse.message) {
          void completeSdaLoginFromMessage(dispatch, dialogResponse.message);
        } else {
          dispatch(clearSession());
        }
        dialog.close();
      });
    });
    return;
  }

  const childWindow = window.open(dialogUrl, "SDA", "height=600,width=500");
  if (!childWindow) {
    onError("The browser blocked the SSO login window.");
    return;
  }
  const expectedOrigin = new URL(dialogUrl).origin;
  const receiveMessage = (event: MessageEvent): void => {
    if (!isTrustedDialogMessage(event, childWindow, expectedOrigin)) {
      return;
    }
    window.removeEventListener("message", receiveMessage);
    if (typeof event.data === "string") {
      void completeSdaLoginFromMessage(dispatch, event.data);
    } else {
      dispatch(clearSession());
    }
    childWindow.close();
  };
  window.addEventListener("message", receiveMessage);
}
